import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Grid,
  Collapse,
  Chip
} from "@mui/material";
import { Icon } from "../../../icons";
import { COLORS, TYPOGRAPHY } from "../../../constants/color";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import { useAuth } from "../../../context/authContext";
import { url } from "../../../constants/url";
import AppToast from "../../../components/AppToast";
import PhotoUploadWithDrawing from "../../../components/siteCheck/PhotoUploadWithDrawing";
import ImagePreviewModal from "../../../components/ui/ImagePreviewModal";
import SignatureCanvas from "../../../components/siteCheck/SignatureCanvas";
import { useNavigate, useParams } from "react-router-dom";
const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": { borderColor: COLORS.PRIMARY },
    "&:hover fieldset": { borderColor: COLORS.PRIMARY },
    "&.Mui-focused fieldset": { borderColor: COLORS.PRIMARY },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
  "& .MuiInputBase-input": { color: COLORS.PRIMARY },
};

const dateInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": { borderColor: `${COLORS.PRIMARY} !important` },
    "&:hover fieldset": { borderColor: `${COLORS.PRIMARY} !important` },
    "&.Mui-focused fieldset": { borderColor: `${COLORS.PRIMARY} !important` },
  },
};

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  if (imagePath.startsWith("data:image")) {
    return imagePath;
  }

  // Convert Windows backslashes to forward slashes and normalize path
  const normalizedPath = imagePath.replace(/\\/g, '/');

  // Remove leading slash if present
  const cleanPath = normalizedPath.startsWith("/")
    ? normalizedPath.substring(1)
    : normalizedPath;
  return `${url}/${cleanPath}`;
};

// Signature Modal Component
const SignatureModal = ({
  open,
  onClose,
  onSave,
  role,
  userData,
  pocName,
  assignedEngineerName,
}) => {
  const canvasRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) {
      if (role === "pmcEngineer") {
        setName(assignedEngineerName || userData?.name || "");
      } else if (role === "siteEngineer") {
        setName(pocName || "");
      } else if (role === "pmc") {
        setName("PMC");
      }

      if (canvasRef.current) {
        canvasRef.current.clear();
        setSignature(null);
      }
    }
  }, [open, role, userData, pocName, assignedEngineerName]);

  const handleClose = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
    setSignature(null);
    onClose();
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setSignature(null);
    }
  };

  const handleSave = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");

      const saveData = {
        signature: dataUrl,
        name: name.trim(),
      };

      onSave(saveData, role);
      handleClose();
    }
  };

  const getModalTitle = () => {
    switch (role) {
      case "pmcEngineer":
        return `Sign as PMC Engineer (${assignedEngineerName || userData?.name || "Engineer"})`;
      case "siteEngineer":
        return `Sign for Site Engineer (${pocName || "POC"})`;
      case "pmc":
        return "Sign as PMC";
      default:
        return "Add Signature";
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          maxHeight: "95vh",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
            {getModalTitle()}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 2 }}>
            <Icon name="close" />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 2,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {(role === "siteEngineer" || role === "pmcEngineer") && (
            <TextField
              fullWidth
              label={`${role === "pmcEngineer" ? "PMC Engineer" : "Site Engineer"} Name *`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${role === "pmcEngineer" ? "PMC Engineer" : "Site Engineer"}'s full name`}
              size="small"
              sx={{ mb: 3 }}
              required
            />
          )}

          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: COLORS.TEXT,
              mb: 1.5,
            }}
          >
            Draw your signature in the box below
          </Typography>

          <Box
            sx={{
              flex: 1,
              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#f8f8f8",
              position: "relative",
              height: 200,
            }}
          >
            <SignatureCanvas
              ref={canvasRef}
              onSignatureChange={setSignature}
              canvasProps={{
                style: {
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} size="small">
            Cancel
          </Button>
          <Button onClick={handleClear} color="error" size="small">
            Clear
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!signature || !name.trim()}
            sx={{
              bgcolor: COLORS.SECONDARY,
              "&.Mui-disabled": {
                bgcolor: COLORS.LIGHT_SECONDARY,
              },
            }}
            size="small"
          >
            Save Signature
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Signatures Display Component
const SignaturesDisplay = ({
  signatures,
  onAddSignature,
  onApplyStoredSignature,
  userRole,
  userData,
  pocName,
  assignedEngineerName,
  userSignature,
  adminSignature,
}) => {
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState(null);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const dateStr = date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
      .replace(/\//g, "/");
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} | ${timeStr}`;
  };

  const shouldDisplaySignature = (role) => {
    return !!signatures?.[role]?.signature;
  };

  const canSign = (role) => {
    const currentUserRole = userRole;

    if (role === "pmcEngineer") {
      return currentUserRole === "ENGINEER" || currentUserRole === "ADMIN" || currentUserRole === "SUPERADMIN";
    } else if (role === "siteEngineer") {
      return currentUserRole === "ENGINEER" || currentUserRole === "ADMIN" || currentUserRole === "SUPERADMIN";
    } else if (role === "pmc") {
      return currentUserRole === "ADMIN" || currentUserRole === "SUPERADMIN";
    }
    return false;
  };

  const hasStoredSignature = (role) => {
    if (role === "pmcEngineer") {
      return userSignature?.hasSignature && userSignature?.signature;
    } else if (role === "pmc") {
      return adminSignature?.hasSignature && adminSignature?.signature;
    }
    return false;
  };

  const getDisplayName = (role, signatureData) => {
    if (signatureData?.name) {
      return signatureData.name;
    }

    switch (role) {
      case "pmcEngineer":
        return assignedEngineerName || "PMC Engineer";
      case "siteEngineer":
        return pocName || "Site Engineer";
      case "pmc":
        return "PMC";
      default:
        return "Not signed";
    }
  };

  const handleSignClick = (role) => {
    if (!canSign(role)) {
      return;
    }

    if (role === "siteEngineer") {
      setCurrentSignatureRole(role);
      setSignatureModalOpen(true);
      return;
    }

    if (hasStoredSignature(role)) {
      onApplyStoredSignature(role);
    } else {
      setCurrentSignatureRole(role);
      setSignatureModalOpen(true);
    }
  };

  const getButtonText = (role) => {
    switch (role) {
      case "pmcEngineer":
        return "Sign as PMC Engineer";
      case "siteEngineer":
        return "Sign for Site Engineer";
      case "pmc":
        return "Sign as PMC";
      default:
        return "Sign";
    }
  };

  const { siteEngineer, pmcEngineer, pmc } = signatures || {};

  return (
    <>
      <Box sx={{ mt: 4 }}>
        <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 3 }}>
          Signatures
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            textAlign: "center",
          }}
        >
          {/* PMC Engineer Signature */}
          <Box>
            {shouldDisplaySignature("pmcEngineer") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(pmcEngineer?.signature)}
                  alt="PMC Engineer Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              PMC Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("pmcEngineer", pmcEngineer)}
            </Typography>
            {pmcEngineer?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(pmcEngineer.signedAt)}
              </Typography>
            )}
            {canSign("pmcEngineer") && !shouldDisplaySignature("pmcEngineer") && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSignClick("pmcEngineer")}
                sx={{ mt: 1 }}
              >
                {getButtonText("pmcEngineer")}
              </Button>
            )}
          </Box>

          {/* Site Engineer Signature */}
          <Box>
            {shouldDisplaySignature("siteEngineer") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(siteEngineer?.signature)}
                  alt="Site Engineer Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              Site Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("siteEngineer", siteEngineer)}
            </Typography>
            {siteEngineer?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(siteEngineer.signedAt)}
              </Typography>
            )}
            {canSign("siteEngineer") &&
              !shouldDisplaySignature("siteEngineer") && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleSignClick("siteEngineer")}
                  sx={{ mt: 1 }}
                >
                  {getButtonText("siteEngineer")}
                </Button>
              )}
          </Box>

          {/* PMC Signature */}
          <Box>
            {shouldDisplaySignature("pmc") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(pmc?.signature)}
                  alt="PMC Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              PMC
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("pmc", pmc)}
            </Typography>
            {pmc?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(pmc.signedAt)}
              </Typography>
            )}
            {canSign("pmc") && !shouldDisplaySignature("pmc") && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSignClick("pmc")}
                sx={{ mt: 1 }}
              >
                {getButtonText("pmc")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Signature Modal */}
      {signatureModalOpen && (
        <SignatureModal
          open={signatureModalOpen}
          onClose={() => {
            setSignatureModalOpen(false);
            setCurrentSignatureRole(null);
          }}
          onSave={(signatureData) => {
            onAddSignature(signatureData, currentSignatureRole);
            setSignatureModalOpen(false);
            setCurrentSignatureRole(null);
          }}
          role={currentSignatureRole}
          userData={userData}
          pocName={pocName}
          assignedEngineerName={assignedEngineerName}
        />
      )}
    </>
  );
};

// Solution Renderer Component
const SolutionRenderer = ({ solution }) => {
  if (!solution) return null;

  try {
    if (typeof solution === 'string' && solution.trim().startsWith('{') && solution.trim().endsWith('}')) {
      const structured = JSON.parse(solution);
      if (structured.isStructured) {
        if (structured.type === 'points' && Array.isArray(structured.data)) {
          return (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {structured.data.map((point, i) => (
                <Typography component="li" key={i} sx={{ color: COLORS.PRIMARY, mb: 0.5 }}>
                  {point}
                </Typography>
              ))}
            </Box>
          );
        }
        if (structured.type === 'table' && Array.isArray(structured.data)) {
          return (
            <Box sx={{ mt: 1, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                <tbody>
                  {structured.data.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, padding: '8px', fontSize: '0.875rem', color: COLORS.PRIMARY }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          );
        }
      }
    }
  } catch (e) {
    // Fallback to text
  }

  return (
    <Typography sx={{ color: COLORS.PRIMARY, whiteSpace: 'pre-wrap' }}>
      {solution}
    </Typography>
  );
};

// Points Editor Component
const PointsEditor = ({ points, setPoints }) => {
  const handleAddPoint = () => setPoints([...points, ""]);
  const handleRemovePoint = (index) => {
    const newPoints = points.filter((_, i) => i !== index);
    setPoints(newPoints.length > 0 ? newPoints : [""]);
  };
  const handleChangePoint = (index, value) => {
    const newPoints = [...points];
    newPoints[index] = value;
    setPoints(newPoints);
  };

  return (
    <Box>
      {points.map((point, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Point ${index + 1}`}
            value={point}
            onChange={(e) => handleChangePoint(index, e.target.value)}
            sx={inputStyles}
          />
          <IconButton onClick={() => handleRemovePoint(index)} color="error" size="small">
            <Icon name="close2" size={18} />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Icon name="plus" />} onClick={handleAddPoint} size="small" sx={{ color: COLORS.SECONDARY, textTransform: 'none' }}>
        Add Point
      </Button>
    </Box>
  );
};

// Table Editor Component
const TableEditor = ({ tableData, setTableData, columns, setColumns }) => {
  const handleAddRow = () => {
    setTableData([...tableData, Array(columns).fill("")]);
  };
  const handleRemoveRow = (index) => {
    const newData = tableData.filter((_, i) => i !== index);
    setTableData(newData.length > 0 ? newData : [Array(columns).fill("")]);
  };
  const handleChangeCell = (rowIndex, colIndex, value) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
  };

  const handleColumnChange = (newCols) => {
    const cols = Math.max(1, Math.min(5, newCols));
    setColumns(cols);
    const newData = tableData.map(row => {
      if (row.length < cols) {
        return [...row, ...Array(cols - row.length).fill("")];
      }
      return row.slice(0, cols);
    });
    setTableData(newData);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Columns:</Typography>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 1, max: 5 }}
          value={columns}
          onChange={(e) => handleColumnChange(parseInt(e.target.value) || 1)}
          sx={{ ...inputStyles, width: 80 }}
        />
      </Box>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {tableData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} style={{ padding: '8px', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      value={cell}
                      onChange={(e) => handleChangeCell(rowIndex, colIndex, e.target.value)}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      placeholder="Cell content"
                      sx={{ "& .MuiInputBase-input": { fontSize: '0.875rem' } }}
                    />
                  </td>
                ))}
                <td style={{ padding: '4px', textAlign: 'center', width: 40 }}>
                  <IconButton onClick={() => handleRemoveRow(rowIndex)} color="error" size="small">
                    <Icon name="close2" size={18} />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Button startIcon={<Icon name="plus" />} onClick={handleAddRow} size="small" sx={{ color: COLORS.SECONDARY, mt: 1, textTransform: 'none' }}>
        Add Row
      </Button>
    </Box>
  );
};

// Entry Form Component (for inline editing)
const EntryForm = ({ index, onSave, onCancel, onAddPhoto, photos = [], initialData }) => {
  const [test, setTest] = useState(initialData?.test || "");
  const [solutionType, setSolutionType] = useState("text"); // text, points, table
  const [solutionText, setSolutionText] = useState("");
  const [solutionPoints, setSolutionPoints] = useState([""]);
  const [solutionTable, setSolutionTable] = useState([["", ""]]);
  const [tableCols, setTableCols] = useState(2);
  const [entryPhotos, setEntryPhotos] = useState(photos);

  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  useEffect(() => {
    setEntryPhotos(photos);
  }, [photos]);

  useEffect(() => {
    if (initialData?.solution) {
      const solution = initialData.solution;
      try {
        if (typeof solution === 'string' && solution.trim().startsWith('{') && solution.trim().endsWith('}')) {
          const structured = JSON.parse(solution);
          if (structured.isStructured) {
            setSolutionType(structured.type);
            if (structured.type === 'points') {
              setSolutionPoints(structured.data);
            } else if (structured.type === 'table') {
              setSolutionTable(structured.data);
              setTableCols(structured.columns || (structured.data[0] ? structured.data[0].length : 2));
            }
          } else {
            setSolutionText(solution);
          }
        } else {
          setSolutionText(solution);
        }
      } catch (e) {
        setSolutionText(solution);
      }
    }
  }, [initialData]);

  const handleSave = () => {
    if (!test.trim()) return;

    let finalSolution = "";
    if (solutionType === "text") {
      if (!solutionText.trim()) return;
      finalSolution = solutionText.trim();
    } else if (solutionType === "points") {
      const filteredPoints = solutionPoints.filter(p => p.trim() !== "");
      if (filteredPoints.length === 0) return;
      finalSolution = JSON.stringify({
        isStructured: true,
        type: "points",
        data: filteredPoints
      });
    } else if (solutionType === "table") {
      const filteredTable = solutionTable.filter(row => row.some(cell => cell.trim() !== ""));
      if (filteredTable.length === 0) return;
      finalSolution = JSON.stringify({
        isStructured: true,
        type: "table",
        columns: tableCols,
        data: filteredTable
      });
    }

    onSave({
      ...initialData,
      test: test.trim(),
      solution: finalSolution,
      photos: entryPhotos,
    });
  };

  const handleRemovePhoto = (photoIndex) => {
    const updatedPhotos = [...entryPhotos];
    updatedPhotos.splice(photoIndex, 1);
    setEntryPhotos(updatedPhotos);
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: `1px solid ${COLORS.PRIMARY}`, borderRadius: 2, bgcolor: '#fff' }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>
        {index + 1}.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1, fontWeight: 600 }}
        >
          Test
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Enter test details..."
          value={test}
          onChange={(e) => setTest(e.target.value)}
          sx={inputStyles}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
          >
            Solution
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['text', 'points', 'table'].map((type) => (
              <Chip
                key={type}
                label={type.charAt(0).toUpperCase() + type.slice(1)}
                onClick={() => setSolutionType(type)}
                size="small"
                sx={{
                  bgcolor: solutionType === type ? COLORS.PRIMARY : 'transparent',
                  color: solutionType === type ? '#fff' : COLORS.PRIMARY,
                  border: `1px solid ${COLORS.PRIMARY}`,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: solutionType === type ? COLORS.PRIMARY : 'rgba(66, 26, 110, 0.04)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {solutionType === "text" && (
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter solution..."
            value={solutionText}
            onChange={(e) => setSolutionText(e.target.value)}
            sx={inputStyles}
          />
        )}

        {solutionType === "points" && (
          <PointsEditor points={solutionPoints} setPoints={setSolutionPoints} />
        )}

        {solutionType === "table" && (
          <TableEditor 
            tableData={solutionTable} 
            setTableData={setSolutionTable} 
            columns={tableCols} 
            setColumns={setTableCols} 
          />
        )}
      </Box>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          textTransform="none"
          startIcon={<Icon name="camera" />}
          onClick={onAddPhoto}
          disabled={entryPhotos?.length >= 5}
          sx={{
            borderColor: COLORS.SECONDARY,
            color: COLORS.SECONDARY,
            "&:hover": {
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
            },
          }}
        >
          Add Photo ({entryPhotos?.length || 0}/5)
        </Button>
      </Box>

      {entryPhotos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1, fontWeight: 600 }}>
            Added Photos
          </Typography>
          <Grid container spacing={2}>
            {entryPhotos.map((photo, photoIndex) => (
              <Grid item xs={12} sm={6} md={4} key={photoIndex}>
                <Box
                  onClick={() => {
                    const imageUrl = photo.imageUrl || (photo.photoPath ? getFullImageUrl(photo.photoPath) : photo);
                    setSelectedImage({ url: imageUrl, title: photo.title || photo.remarks || "" });
                    setPreviewOpen(true);
                  }}
                  sx={{
                    position: "relative",
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    height: "180px",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#f5f5f5",
                    cursor: "zoom-in",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: COLORS.PRIMARY,
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={photo.imageUrl || (photo.photoPath ? getFullImageUrl(photo.photoPath) : photo)}
                    alt={`Photo ${photoIndex + 1}`}
                    sx={{
                      width: "100%",
                      height: (photo.title || photo.remarks) ? "130px" : "100%",
                      objectFit: "contain",
                    }}
                  />
                  {(photo.title || photo.remarks) && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        color: COLORS.TEXT,
                        p: 1,
                        borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {photo.title || photo.remarks}
                      </Typography>
                    </Box>
                  )}
                  <IconButton
                    onClick={() => handleRemovePhoto(photoIndex)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 0, 0, 0.7)",
                      color: "#fff",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.9)",
                      },
                    }}
                    size="small"
                  >
                    <Icon name="close" size={14} />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Button Container - Cancel left, Save right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mt: 3,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          startIcon={<Icon name="close2" size={18} />}
          sx={{
            height: 36,
            minWidth: 110,
            px: 2,
            color: COLORS.ERROR,
            borderColor: COLORS.ERROR,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              borderColor: COLORS.ERROR,
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          startIcon={<Icon name="check" size={18} />}
          variant="contained"
          disabled={!test.trim() || (solutionType === 'text' && !solutionText.trim())}
          sx={{
            height: 36,
            minWidth: 110,
            px: 2,
            bgcolor: COLORS.SECONDARY,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              bgcolor: COLORS.PRIMARY,
            },
            "&.Mui-disabled": {
              bgcolor: COLORS.LIGHT_SECONDARY,
              color: "#fff",
            },
          }}
        >
          {initialData ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={selectedImage.url}
        title={selectedImage.title}
      />
    </Box>
  );
};

// Display Entry Component
const DisplayEntry = ({ entry, index, onEdit, onDelete, catIdx }) => {
  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(66, 26, 110, 0.02)', borderRadius: 2, borderLeft: `4px solid ${COLORS.PRIMARY}` }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontWeight: 700 }}>
          {index + 1}. {entry.test}
        </Typography>
        <Box>
          <IconButton
            onClick={() => onEdit(catIdx, index)}
            size="small"
            sx={{ color: COLORS.SECONDARY, mt: -0.5 }}
          >
            <Icon name="edit" size={20} />
          </IconButton>
          <IconButton
            onClick={() => onDelete(catIdx, index)}
            size="small"
            sx={{ color: COLORS.ERROR, mt: -0.5 }}
          >
            <Icon name="close2" size={20} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 2, pl: 2 }}>
        <SolutionRenderer solution={entry.solution} />
      </Box>

      {entry.photos?.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Grid container spacing={2}>
            {entry.photos.map((photo, photoIndex) => {
              const imageUrl = photo.imageUrl || (photo.photoPath ? getFullImageUrl(photo.photoPath) : photo);
              return (
                <Grid item xs={12} sm={6} md={4} key={photoIndex}>
                  <Box
                    onClick={() => {
                      setSelectedImage({ url: imageUrl, title: photo.title || photo.remarks || "" });
                      setPreviewOpen(true);
                    }}
                    sx={{
                      position: "relative",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      overflow: "hidden",
                      height: "180px",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "#f5f5f5",
                      cursor: "zoom-in",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                        borderColor: COLORS.PRIMARY,
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={`Photo ${photoIndex + 1}`}
                      sx={{
                        width: "100%",
                        height: (photo.title || photo.remarks) ? "130px" : "100%",
                        objectFit: "contain",
                      }}
                    />
                  {(photo.title || photo.remarks) && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        color: COLORS.TEXT,
                        p: 1,
                        borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {photo.title || photo.remarks}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
        </Box>
      )}

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={selectedImage.url}
        title={selectedImage.title}
      />
    </Box>
  );
};

// Main Component - Full Screen Edit
const EditSelfVisitReport = () => {
  const { token, userData, hasRole } = useAuth();
  const navigate = useNavigate();
  const { reportId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  // Main form fields
  const [projectName, setProjectName] = useState("");
  const [location, setLocation] = useState("");
  const [dateOfVisit, setDateOfVisit] = useState(null);
  const [developers, setDevelopers] = useState("");
  const [pmc, setPmc] = useState("Arconest Consultants");
  const [purposeOfVisit, setPurposeOfVisit] = useState("");

  // Entries
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIdx, setSelectedCategoryIdx] = useState(0);
  const [newCategoryName, setNewCategoryName] = useState("");
  
  const [editingEntryPos, setEditingEntryPos] = useState(null);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [newEntryPhotos, setNewEntryPhotos] = useState([]);

  // Signatures
  const [signatures, setSignatures] = useState({
    siteEngineer: null,
    pmcEngineer: null,
    pmc: null,
  });

  // Stored signatures
  const [userSignature, setUserSignature] = useState(null);
  const [adminSignature, setAdminSignature] = useState(null);
  const [status, setStatus] = useState("DRAFT");
  const [pocName, setPocName] = useState("");
  const [assignedEngineerName, setAssignedEngineerName] = useState("");
  const statusStyles = {
    SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
    FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
    DRAFT: { bgcolor: "#F8D66D", color: "#fff" },
  };
  const showToast = (type, title, message) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  useEffect(() => {
    if (reportId) {
      fetchReportData();
      fetchUserSignature();
      setAssignedEngineerName(userData?.name || "Engineer");
    }
  }, [reportId]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${url}/api/self-visit/fetch/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const reportData = response.data.data;

      // Set basic information
      setProjectName(reportData.projectName || "");
      setLocation(reportData.location || "");
      setDateOfVisit(reportData.dateOfVisit ? new Date(reportData.dateOfVisit) : null);
      setDevelopers(reportData.developers || "");
      setPmc(reportData.pmc || "Arconest Consultants");
      setPurposeOfVisit(reportData.purposeOfVisit || "");
      setStatus(reportData.status || "DRAFT");
      // Process entries - Handle different data structures
      let loadedCategories = [];
      
      const formatEntry = (entry, index) => {
        const testContent = entry.details || entry.test || entry.title || entry.question || "";
        const solutionContent = entry.remarks || entry.solution || entry.answer || "";
        return {
          title: entry.title || `Test ${index + 1}`,
          test: testContent,
          solution: solutionContent,
          photos: (entry.photos || []).map(photo => ({
            ...photo,
            photoPath: getFullImageUrl(photo.photoPath),
            imageUrl: getFullImageUrl(photo.photoPath),
            remarks: photo.remarks || "",
            title: photo.remarks || ""
          }))
        };
      };

      if (reportData.categories && Array.isArray(reportData.categories) && reportData.categories.length > 0) {
        loadedCategories = reportData.categories.map(cat => ({
          name: cat.name || "Uncategorized",
          entries: (cat.entries || []).map(formatEntry)
        }));
      } else if (reportData.entries && Array.isArray(reportData.entries) && reportData.entries.length > 0) {
        loadedCategories = [{
          name: "Uncategorized",
          entries: reportData.entries.map(formatEntry)
        }];
      }

      setCategories(loadedCategories);

      // Set signatures
      if (reportData.signatures) {
        const formattedSignatures = {};
        if (reportData.signatures.siteEngineer) {
          formattedSignatures.siteEngineer = {
            ...reportData.signatures.siteEngineer,
            signature: getFullImageUrl(reportData.signatures.siteEngineer.signature)
          };
        }
        if (reportData.signatures.pmcEngineer) {
          formattedSignatures.pmcEngineer = {
            ...reportData.signatures.pmcEngineer,
            signature: getFullImageUrl(reportData.signatures.pmcEngineer.signature)
          };
        }
        if (reportData.signatures.pmc) {
          formattedSignatures.pmc = {
            ...reportData.signatures.pmc,
            signature: getFullImageUrl(reportData.signatures.pmc.signature)
          };
        }
        setSignatures(formattedSignatures);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching report data:", error);
      showToast("error", "Error", "Failed to load report data");
      setLoading(false);
    }
  };

  const fetchUserSignature = async () => {
    try {
      const userRole = getUserRole();

      if (
        userRole === "ENGINEER" ||
        userRole === "ADMIN" ||
        userRole === "SUPERADMIN"
      ) {
        const response = await axios.get(
          `${url}/api/users/signature/${userData._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.data) {
          if (userRole === "ENGINEER") {
            setUserSignature(response.data.data);
          } else if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
            setAdminSignature(response.data.data);
          }
        }
      }
    } catch (err) {
      console.log("Could not fetch user signature:", err.message);
    }
  };

  const handlePhotoUpload = (photoData) => {
    const newPhoto = {
      imageUrl: photoData.imageUrl,
      title: photoData.title || "",
      remarks: photoData.title || "",
      description: photoData.title || "", // Extra fallback
    };

    setNewEntryPhotos(prev => [...prev, newPhoto]);
    setPhotoModalOpen(false);
    showToast("success", "Photo Added", "Photo uploaded successfully.");
  };

  // Add new entry
  const handleAddNewEntry = (catIdx) => {
    setSelectedCategoryIdx(catIdx);
    setEditingEntryPos(null);
    setShowNewEntryForm(true);
    setNewEntryPhotos([]);
  };

  // Edit existing entry
  const handleEditEntry = (catIdx, entryIdx) => {
    setEditingEntryPos({ catIdx, entryIdx });
    setShowNewEntryForm(false);
    setNewEntryPhotos([]); 
  };

  // Save entry
  const handleSaveEntry = (entryData) => {
    const { removedPhotoIds = [], ...restEntryData } = entryData;

    setCategories(prev => {
      const newCats = [...prev];
      if (editingEntryPos !== null) {
        const { catIdx, entryIdx } = editingEntryPos;
        const currentEntry = newCats[catIdx].entries[entryIdx];
        const currentRemovedIds = currentEntry.removedPhotoIds || [];
        const newRemovedIds = [...currentRemovedIds, ...removedPhotoIds];
        const uniqueRemovedIds = [...new Set(newRemovedIds)];

        newCats[catIdx].entries[entryIdx] = {
          ...currentEntry,
          ...restEntryData,
          removedPhotoIds: uniqueRemovedIds
        };
      } else {
        newCats[selectedCategoryIdx].entries.push({
          ...restEntryData,
          photos: entryData.photos
        });
      }
      return newCats;
    });

    setEditingEntryPos(null);
    setShowNewEntryForm(false);
    setNewEntryPhotos([]);
  };

  // Cancel entry form
  const handleCancelEntry = () => {
    setEditingEntryPos(null);
    setShowNewEntryForm(false);
    setNewEntryPhotos([]);
  };

  // Add photo to current entry
  const handleAddPhotoToEntry = () => {
    setPhotoModalOpen(true);
  };

  // Delete entry
  const handleDeleteEntry = (catIdx, entryIdx) => {
    setCategories(prev => {
      const newCats = [...prev];
      newCats[catIdx].entries = newCats[catIdx].entries.filter((_, i) => i !== entryIdx);
      return newCats;
    });
  };

  // Add Category
  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    const existingIdx = categories.findIndex(c => c.name.toLowerCase() === name.toLowerCase());

    if (existingIdx !== -1) {
      setSelectedCategoryIdx(existingIdx);
      setShowNewEntryForm(true);
      setEditingEntryPos(null);
    } else {
      setCategories(prev => {
        const updated = [...prev, { name, entries: [] }];
        setSelectedCategoryIdx(updated.length - 1);
        return updated;
      });
      setShowNewEntryForm(true);
      setEditingEntryPos(null);
    }
    setNewCategoryName("");
  };

  // Delete Category
  const handleDeleteCategory = (idx) => {
    setCategories(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      setSelectedCategoryIdx(Math.max(0, idx - 1));
      return updated;
    });
    setShowNewEntryForm(false);
    setEditingEntryPos(null);
  };

  // Handle download report
  const handleDownloadReport = async () => {
    try {
      if (!token || !reportId) {
        showToast("error", "Error", "Unable to download report");
        return;
      }

      setDownloading(true);

      const response = await axios.get(
        `${url}/api/self-visit/download-report/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const date = dateOfVisit || new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const cleanProjectName = (projectName || "SiteVisit")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "")
        .substring(0, 50);

      link.download = `SVR_${cleanProjectName}_${year}_${month}_${day}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showToast("success", "Success", "Report downloaded successfully");
    } catch (err) {
      console.error("Error downloading report:", err);
      showToast("error", "Error", err.response?.data?.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  // Handle signature addition
  const handleAddSignature = (signatureData, role) => {
    setSignatures((prev) => ({
      ...prev,
      [role]: {
        signature: signatureData.signature,
        name: signatureData.name || "",
        signedAt: new Date().toISOString(),
      },
    }));

    if (role === "pmcEngineer" || role === "pmc") {
      saveSignatureToUserProfile(signatureData, role);
    }

    showToast("success", "Signature Added", `${role === "pmcEngineer" ? "PMC Engineer" : role === "siteEngineer" ? "Site Engineer" : "PMC"} signature added successfully.`);
  };

  const saveSignatureToUserProfile = async (signatureData, role) => {
    try {
      const blob = dataURLtoBlob(signatureData.signature);
      const formData = new FormData();
      formData.append("signature", blob, "signature.png");

      await axios.post(
        `${url}/api/users/signature/${userData._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const userRole = getUserRole();
      if (userRole === "ENGINEER") {
        setUserSignature({
          hasSignature: true,
          signature: signatureData.signature,
          userName: signatureData.name || userData?.name || "Engineer",
        });
      } else if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
        setAdminSignature({
          hasSignature: true,
          signature: signatureData.signature,
          userName: signatureData.name || "PMC",
        });
      }
    } catch (err) {
      console.error("Error saving signature to profile:", err);
    }
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleApplyStoredSignature = (role) => {
    let storedSignature = null;
    let userName = "";

    if (role === "pmcEngineer" && userSignature?.hasSignature) {
      storedSignature = userSignature.signature;
      userName = userSignature.userName || assignedEngineerName || "PMC Engineer";
    } else if (role === "pmc" && adminSignature?.hasSignature) {
      storedSignature = adminSignature.signature;
      userName = adminSignature.userName || "PMC";
    }

    if (storedSignature) {
      setSignatures((prev) => ({
        ...prev,
        [role]: {
          signature: storedSignature,
          name: userName,
          signedAt: new Date().toISOString(),
        },
      }));

      showToast("success", "Signature Applied", "Your stored signature has been applied.");
    }
  };

  // Handle form submission (Update)
  // Handle form submission (Update)
  const handleSubmit = async () => {
    if (!projectName.trim()) {
      showToast("error", "Validation Error", "Please enter Project Name");
      return;
    }
    if (!location.trim()) {
      showToast("error", "Validation Error", "Please enter Location");
      return;
    }
    if (!dateOfVisit) {
      showToast("error", "Validation Error", "Please select Date of Visit");
      return;
    }
    if (!developers.trim()) {
      showToast("error", "Validation Error", "Please enter Developers");
      return;
    }
    if (!purposeOfVisit.trim()) {
      showToast("error", "Validation Error", "Please enter Purpose of Visit");
      return;
    }
    if (categories.every(cat => cat.entries.length === 0)) {
      showToast("error", "Validation Error", "Please add at least one entry");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      // Add text fields
      formData.append("projectName", projectName);
      formData.append("location", location);
      formData.append("dateOfVisit", dateOfVisit.toISOString());
      formData.append("developers", developers);
      formData.append("pmc", pmc);
      formData.append("purposeOfVisit", purposeOfVisit);
      formData.append("reportDate", new Date().toISOString());

      // Map categories to backend format
      const formattedCategories = categories.map((cat) => {
        return {
          name: cat.name,
          entries: cat.entries.map((entry, index) => {
            const photosToKeep = (entry.photos || []).filter(photo => {
              if (entry.removedPhotoIds?.includes(photo._id)) {
                return false;
              }
              return true;
            });

            return {
              title: entry.title || `Test ${index + 1}`,
              details: entry.test || "",
              remarks: entry.solution || "",
              photos: photosToKeep.map((photo) => {
                const originalIndex = entry.photos.findIndex(p => p === photo);
                return {
                  _id: photo._id,
                  photoPath: photo.photoPath || "",
                  remarks: photo.remarks || photo.title || photo.description || "",
                  tempIndex: originalIndex,
                  isNew: (!photo.photoPath || photo.photoPath.startsWith('data:')) &&
                         (photo.imageUrl && photo.imageUrl.startsWith('data:'))
                };
              }),
              removedPhotoIds: entry.removedPhotoIds || []
            };
          })
        };
      });

      // Add categories as JSON string
      formData.append("categories", JSON.stringify(formattedCategories));

      // Add signatures as JSON string
      const formattedSignatures = {};
      if (signatures.siteEngineer) {
        formattedSignatures.siteEngineer = {
          name: signatures.siteEngineer.name || pocName || "Site Engineer",
          signature: signatures.siteEngineer.signature,
          signedAt: signatures.siteEngineer.signedAt,
        };
      }
      if (signatures.pmcEngineer) {
        formattedSignatures.pmcEngineer = {
          name: signatures.pmcEngineer.name || assignedEngineerName || "PMC Engineer",
          signature: signatures.pmcEngineer.signature,
          signedAt: signatures.pmcEngineer.signedAt,
        };
      }
      if (signatures.pmc) {
        formattedSignatures.pmc = {
          name: signatures.pmc.name || "PMC",
          signature: signatures.pmc.signature,
          signedAt: signatures.pmc.signedAt,
        };
      }

      formData.append("signatures", JSON.stringify(formattedSignatures));

      if (userData?._id) {
        formData.append("userID", userData._id);
      }

      // Add new photos as separate files
      let flatEntryIdx = 0;
      categories.forEach((cat) => {
        cat.entries.forEach((entry) => {
          entry.photos?.forEach((photo, photoIndex) => {
            if (!entry.removedPhotoIds?.includes(photo._id) &&
              photo.imageUrl &&
              photo.imageUrl.startsWith("data:")) {
              try {
                const blob = dataURLtoBlob(photo.imageUrl);
                const file = new File(
                  [blob],
                  `photo_${flatEntryIdx}_${photoIndex}.png`,
                  { type: "image/png" }
                );
                formData.append(`entries[${flatEntryIdx}][photos]`, file);
              } catch (err) {
                console.error("Error converting photo to blob:", err);
              }
            }
          });
          flatEntryIdx++;
        });
      });

      const response = await axios.put(
        `${url}/api/self-visit/update/${reportId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        }
      );

      if (response.status === 200) {
        showToast("success", "Success!", "Site Visit report updated successfully!");

        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
    } catch (error) {
      console.error("Error updating form:", error);
      showToast(
        "error",
        "Update Failed",
        error.response?.data?.message || "Failed to update Site Visit report"
      );
    } finally {
      setSaving(false);
    }
  };

  const getUserRole = () => {
    if (hasRole("CLIENT")) return "CLIENT";
    if (hasRole("ADMIN")) return "ADMIN";
    if (hasRole("SUPERADMIN")) return "SUPERADMIN";
    if (hasRole("ENGINEER")) return "ENGINEER";
    return null;
  };

  const userRole = getUserRole();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 1 }}>
        {/* Header with Back Button and Edit Icon */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          position: "relative"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
              Edit Site Visit Report
            </Typography>

            {/* Status Badge */}
            <Chip
              label={status === "SIGNPENDING" ? "Sign Pending" :
                status === "FINALREPORT" ? "Final Report" : "Draft"}
              size="small"
              sx={{
                ...(statusStyles[status] || statusStyles.DRAFT),
                height: 28,
                ...TYPOGRAPHY.BODY_SMALL,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: 'uppercase',
                '& .MuiChip-label': {
                  padding: '0 12px',
                  letterSpacing: '0.5px'
                }
              }}
            />
          </Box>

          {/* Right side icons */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {/* Download Button - Only for FINALREPORT status */}
            {status === "FINALREPORT" && (
              <Button
                variant="contained"
                onClick={handleDownloadReport}
                startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <Icon name="download1" size={18} />}
                disabled={downloading}
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  color: "#fff",
                  px: 2,
                  py: 0.8,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: 140,
                  "&:hover": { bgcolor: "#5a1c7e" },
                }}
              >
                {downloading ? "Downloading..." : "Download PDF"}
              </Button>
            )}

            {/* Close Icon */}
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: "red",
                "&:hover": {
                  backgroundColor: "rgba(255, 0, 0, 0.1)",
                }
              }}
            >
              <Icon name="close" />
            </IconButton>
          </Box>
        </Box>

        <Paper sx={{ p: 3, backgroundColor: "transparent", boxShadow: "none" }}>
          {/* Basic Information */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 3 }}>
              Basic Information
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Project Name *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter project name"
                  size="small"
                  sx={inputStyles}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Location *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter location"
                  size="small"
                  sx={inputStyles}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Date of Visit *
                </Typography>
                <DatePicker
                  value={dateOfVisit}
                  onChange={(newValue) => setDateOfVisit(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Select date"
                      sx={dateInputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small">
                              <Icon name="calendar2" color={COLORS.PRIMARY} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Developers *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter developers"
                  size="small"
                  sx={inputStyles}
                  value={developers}
                  onChange={(e) => setDevelopers(e.target.value)}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  PMC
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter PMC"
                  size="small"
                  sx={inputStyles}
                  value={pmc}
                  onChange={(e) => setPmc(e.target.value)}
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Purpose of Visit *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter purpose of visit"
                  sx={inputStyles}
                  value={purposeOfVisit}
                  onChange={(e) => setPurposeOfVisit(e.target.value)}
                  required
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Entries Section */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 3 }}>
              Site Visit Entries
            </Typography>

            {categories.length === 0 && (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                  borderRadius: 1,
                  mb: 3,
                }}
              >
                <Typography sx={{ color: COLORS.TEXT, mb: 1 }}>
                  No categories yet
                </Typography>
                <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 14 }}>
                  Type a category name below and click "+ Add Category" to get started
                </Typography>
              </Box>
            )}

            {categories.map((cat, catIdx) => (
              <Box 
                key={catIdx} 
                sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}33`, borderRadius: 2, p: 3, mb: 4 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                    {cat.name}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCategory(catIdx)}
                  >
                    <Icon name="delete" size={20} />
                  </IconButton>
                </Box>

                {/* Saved entries for this category */}
                {cat.entries.map((entry, index) => (
                  <React.Fragment key={index}>
                    <DisplayEntry
                      entry={entry}
                      index={index}
                      catIdx={catIdx}
                      onDelete={handleDeleteEntry}
                      onEdit={handleEditEntry}
                    />
                    {/* Inline Edit Form */}
                    <Collapse in={editingEntryPos?.catIdx === catIdx && editingEntryPos?.entryIdx === index}>
                      {editingEntryPos?.catIdx === catIdx && editingEntryPos?.entryIdx === index && (
                        <EntryForm
                          index={index}
                          onSave={handleSaveEntry}
                          onCancel={handleCancelEntry}
                          onAddPhoto={handleAddPhotoToEntry}
                          photos={[...(entry.photos || []), ...newEntryPhotos]}
                          initialData={entry}
                        />
                      )}
                    </Collapse>
                  </React.Fragment>
                ))}

                {/* New Entry Form */}
                <Collapse in={showNewEntryForm && selectedCategoryIdx === catIdx && editingEntryPos === null}>
                  {showNewEntryForm && selectedCategoryIdx === catIdx && editingEntryPos === null && (
                    <EntryForm
                      index={cat.entries.length}
                      onSave={handleSaveEntry}
                      onCancel={handleCancelEntry}
                      onAddPhoto={handleAddPhotoToEntry}
                      photos={newEntryPhotos}
                    />
                  )}
                </Collapse>

                {/* Empty State */}
                {cat.entries.length === 0 && (!showNewEntryForm || selectedCategoryIdx !== catIdx) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: COLORS.TEXT, mb: 1 }}>
                      No entries added yet
                    </Typography>
                    <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 14 }}>
                      Click "Add New Entry" to start adding test entries
                    </Typography>
                  </Box>
                )}

                {/* Add entry button */}
                {(!showNewEntryForm || selectedCategoryIdx !== catIdx) && editingEntryPos === null && (
                  <Box sx={{ display: "flex", justifyContent: cat.entries.length > 0 ? "flex-end" : "center", mt: 2 }}>
                    <Button
                      variant={cat.entries.length > 0 ? "outlined" : "contained"}
                      startIcon={<Icon name="plus2" />}
                      onClick={() => handleAddNewEntry(catIdx)}
                      sx={
                        cat.entries.length > 0
                          ? {
                              borderColor: COLORS.SECONDARY,
                              color: COLORS.SECONDARY,
                              textTransform: "none",
                              "&:hover": { borderColor: COLORS.PRIMARY, color: COLORS.PRIMARY },
                            }
                          : {
                              bgcolor: COLORS.SECONDARY,
                              textTransform: "none",
                              "&:hover": { bgcolor: COLORS.PRIMARY },
                            }
                      }
                    >
                      {cat.entries.length > 0 ? "Add Another Entry" : "Add New Entry"}
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            {/* ── Add Category Row (At the bottom) ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 2 }}>
              <TextField
                placeholder="Select or enter new category…"
                size="small"
                sx={{ ...inputStyles, width: 250 }}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
              />
              <Button
                variant="contained"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  "&:hover": { bgcolor: COLORS.PRIMARY },
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                + Add / Select Category
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Signatures Section */}
          <SignaturesDisplay
            signatures={signatures}
            onAddSignature={handleAddSignature}
            onApplyStoredSignature={handleApplyStoredSignature}
            userRole={userRole}
            userData={userData}
            pocName={pocName}
            assignedEngineerName={assignedEngineerName}
            userSignature={userSignature}
            adminSignature={adminSignature}
          />

          {/* Update Button at Center Below Signatures */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                "&:hover": {
                  bgcolor: COLORS.PRIMARY,
                },
              }}
              onClick={handleSubmit}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Icon name="save" />}
              disabled={saving || editingEntryPos !== null}
            >
              {saving ? "Saving..." : "Update Site Visit Report"}
            </Button>
          </Box>
        </Paper>

        {/* Photo Upload Modal */}
        {photoModalOpen && (
          <PhotoUploadWithDrawing
            open={photoModalOpen}
            onClose={() => setPhotoModalOpen(false)}
            onSave={handlePhotoUpload}
            maxPhotos={5}
          />
        )}

        {/* Toast */}
        <AppToast
          open={toastOpen}
          type={toastType}
          title={toastTitle}
          message={toastMessage}
          onClose={handleToastClose}
          autoHideDuration={5000}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default EditSelfVisitReport;